import { z } from "zod";
import apiClient from "../../../shared/api/client";
import type { AuthSession } from "../types";

const userSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  name: z.string().min(1),
  roles: z.array(z.string()),
  twoFactorEnabled: z.boolean(),
});

/**
 * Login/setup responses still include accessToken in the body for backward
 * compatibility with non-browser admin tooling, but the browser flow ignores
 * it — the backend sets `eriol_session_admin` HttpOnly cookie on the same
 * response and that's what subsequent calls ride on.
 */
const sessionSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  user: userSchema,
});

const twoFactorRequiredSchema = z.object({
  requiresTwoFactor: z.literal(true),
});

const loginResponseSchema = z.union([sessionSchema, twoFactorRequiredSchema]);

const currentUserSchema = z.object({
  user: userSchema,
});

export type LoginCredentials = {
  email: string;
  password: string;
  totpCode?: string;
};

export type LoginResult = { requiresTwoFactor: true } | AuthSession;

export const adminLogin = async (
  credentials: LoginCredentials,
): Promise<LoginResult> => {
  const response = await apiClient.post("/admin/login", credentials);
  const result = loginResponseSchema.parse(response.data);

  if ("requiresTwoFactor" in result) {
    return { requiresTwoFactor: true };
  }

  return result;
};

export const adminLogout = async (): Promise<void> => {
  await apiClient.post("/admin/logout");
};

export const fetchCurrentUser = async (): Promise<AuthSession["user"]> => {
  // Admin sessions are type="admin"; /auth/me is user-only (rejects admins with
  // 401), which used to log admins out on every reload/deploy. Bootstrap against
  // the admin endpoint instead.
  const response = await apiClient.get("/admin/profile");
  const { user } = currentUserSchema.parse(response.data);
  return user;
};

export const forgotPassword = async (email: string): Promise<void> => {
  await apiClient.post("/admin/forgot-password", { email });
};

export const verifyResetToken = async (token: string): Promise<boolean> => {
  try {
    const response = await apiClient.get(`/admin/reset-password/${token}`);
    return response.data.valid;
  } catch {
    return false;
  }
};

export const resetPassword = async (
  token: string,
  password: string,
): Promise<void> => {
  await apiClient.post("/admin/reset-password", { token, password });
};

export const getGoogleAuthUrl = async (state?: string): Promise<string> => {
  const params = state ? { state } : {};
  const response = await apiClient.get("/admin/google/auth-url", { params });
  return response.data.url;
};

export const googleLogin = async (code: string): Promise<AuthSession> => {
  const response = await apiClient.post("/admin/google/login", { code });
  return sessionSchema.parse(response.data);
};

export const checkSetupStatus = async (): Promise<{ needsSetup: boolean }> => {
  const response = await apiClient.get("/admin/setup/status");
  return response.data;
};

export type SetupAdminInput = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
};

export const setupFirstAdmin = async (
  data: SetupAdminInput,
): Promise<AuthSession> => {
  const response = await apiClient.post("/admin/setup", data);
  return sessionSchema.parse(response.data);
};

export const setupGoogleLogin = async (code: string): Promise<AuthSession> => {
  const response = await apiClient.post("/admin/setup/google", { code });
  return sessionSchema.parse(response.data);
};
